/* global G */
import { useCallback, useContext, useEffect, useState } from 'react'
import { Grid } from '@mui/material'
import { isArr, isObj, PlatformEvent } from 'lib/util'
import TextField from 'ui/Element/Field/Text'
import CheckField from 'ui/Element/Field/Check'
import RadioGroupField from 'ui/Element/Field/Group/Radio'
import DatePickerField from 'ui/Element/Field/DatePicker'
import SelectField from 'ui/Element/Field/Select'
import NumberField from 'ui/Element/Field/Number'
import ApplicationContext from 'platform/react/context/application'
import TimePickerField from 'ui/Element/Field/TimePicker'
import Markdown from 'ui/Element/Text/Markdown'

/**
 * Enumeration of elements representable by {@link DynamicItem}.
 *
 * @readonly
 * @typedef {Readonly<Object<string, Function|React.ReactNode>>} DynamicItemTypes
 */
/**
 * @type {DynamicItemTypes}
 */
const DynamicItemTypes = Object.freeze({
  section: ({ size, children, items = children, events, value = {}, helperText, ...props }) => (
    <Grid
      item
      container
      {...props}
      sm={size}
      xs={12}
    >
      {items?.map(item => (
        <DynamicItem
          key={item.key}
          item={item}
          value={value[item.key]}
          events={events}
        />
      ))}
    </Grid>
  ),
  space: ({ size }) => <Grid item sm={size} xs={12} />,
  paragraph: ({ text, ...props }) => <Markdown {...props}>{text}</Markdown>,
  text: props => <TextField {...props} clearable={true} />,
  check: props => (
    <CheckField
      {...props}
      helperText={props.helperText === null ? null : props.helperText || '\u00A0'}
    />
  ),
  radio: props => <RadioGroupField {...props} />,
  number: ({ rangeError, ...props }) => <NumberField {...props} clearable={true} />,
  select: ({ selection, options = selection, ...props }) => (
    <SelectField {...props} options={[{ key: null, value: '' }, ...options]} />
  ),
  date: ({ rangeError, invalidError, ...props }) => (
    <DatePickerField
      outOfRangeLabel={rangeError}
      invalidLabel={invalidError}
      {...props}
    />
  ),
  time: ({ rangeError, invalidError, ...props }) => (
    <TimePickerField
      outOfRangeLabel={rangeError}
      invalidLabel={invalidError}
      views={['hours', 'minutes']}
      {...props}
    />
  ),
  error: key => () => (
    <Grid item padding={1}>
      <Markdown>{`"${key}" type is unknown`}</Markdown>
    </Grid>
  ),
})

/**
 * If `value` property is not an object, returns it as is. If it is an object and has the property
 * with name `language`, returns that property's value. Otherwise, returns its first property.
 *
 * @param {any} value       the property from which to attempt to obtain a translation
 * @param {string} language the language into which the translation is to be obtained
 * @returns {*}
 */
const translate = (value, language) => (value && isObj(value)
  ? value[language] || value[Object.keys(value)[0]]
  : value)

/**
 * Attempts to translate all properties from `item`. A translatable property should have an object
 * as its value whose properties are [language iso] => [translated string] pairs. Properties that
 * don't have an object as their value are simply left as they are.
 *
 * If a item's property is an objects array, it attempts to translate all their `value` properties.
 *
 * @example
 * {
 *   "en": "Label",
 *   "de": "Etikett"
 * }
 *
 * @param {string} language
 * @returns {(DynamicItemConfig) => DynamicItemConfig}
 */
const translator = language => item => Object.keys(item).reduce((acc, key) => {
  const value = item[key]
  const translatedValue = value && isArr(value)
    ? value.map(option => (option?.value
      ? { ...option, value: translate(option.value, language) }
      : option))
    : translate(value, language)

  return { ...acc, [key]: translatedValue }
}, {})

/**
 * Returns the translated `props`.
 *
 * @param {DynamicItemConfig} [props]
 * @returns {DynamicItemConfig}
 */
const useTranslator = (props) => {
  const { session } = useContext(ApplicationContext)
  const language = session[G.LANGUAGE]
  const translateProps = translator(language)
  return translateProps(props)
}

/**
 * DynamicItem component
 *
 * Dynamically renders one of {@link DynamicItemTypes} depending on `props.item.type` and passes
 * all other `props` to the rendered element.
 *
 * @param {Object} [props]
 * @param {DynamicItemConfig} [props.item={}] props of the item to be rendered
 * @param {Object} [props.events={}]          component's event handlers
 * @param {Function} [props.events.onChange]  triggered whenever the rendered item triggers it
 * @param {any} [props.value]                 rendered item's value
 * @returns {*}
 */
const DynamicItem = ({ item = {}, events: { onChange } = {}, value }) => {
  const { key, type, defaultValue, size, hidden, ...elementProps } = item
  const [itemValue, setItemValue] = useState(value)
  const translatedProps = useTranslator(/** @type DynamicItemConfig */elementProps)

  const Item = DynamicItemTypes[type] || DynamicItemTypes.error(key)
  const { helperText, errorText, ...itemProps } = translatedProps

  useEffect(() => {
    setItemValue(value)
  }, [value])

  const handleChange = useCallback((event) => {
    const nested = event.detail?.item
    const newValue = event.detail?.value ?? event.target?.value
    const checked = event.detail?.checked ?? event.target?.checked

    setItemValue((prevValue) => {
      if (nested) {
        const nestedValues = { ...prevValue, [nested.key]: newValue }
        onChange?.(new PlatformEvent(event, { item, value: nestedValues }))
        return nestedValues
      }

      onChange?.(new PlatformEvent(event, { item, value: newValue ?? null, checked }))
      return newValue
    })
  }, [onChange])

  return hidden ? null : (
    <Grid
      item
      sm={size}
      xs={12}
    >
      <Item
        key={key}
        {...itemProps}
        value={itemValue ?? defaultValue ?? ''}
        events={{ onChange: handleChange }}
        helperText={errorText || helperText}
      />
    </Grid>
  )
}

export default DynamicItem
